import { useWriteQueryParam } from '@consigli/hooks';
import { Finding, FindingCommenter, FindingStatus } from '@consigli/types';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegCheckCircle } from 'react-icons/fa';
import { FiClock } from 'react-icons/fi';
import { LuSend } from 'react-icons/lu';
import { RiDeleteBinLine } from 'react-icons/ri';
import { RiEyeOffLine } from 'react-icons/ri';
import { TbFileSearch } from 'react-icons/tb';

import { DismissibleDisclaimer } from '@/components/dismissible-disclaimer';
import { ServiceHeader } from '@/components/service-header';

import { ExportButton } from './components/export-button';
import { useFindingsContext } from './findings-context';
import { FindingsList } from './findings-list';
import { FindingBlob } from './findings-wrapper';

type MapActionNameToStatesType = {
  [key in string]: {
    icon: React.FC<React.SVGProps<SVGSVGElement>> | undefined;
    status: FindingStatus;
    title: string;
  };
};

export const mapActionNameToStates: MapActionNameToStatesType = {
  [FindingStatus.NOT_RELEVANT]: {
    icon: RiEyeOffLine,
    status: FindingStatus.NOT_RELEVANT,
    title: 'findings.states.notrelevant',
  },
  [FindingStatus.ACTIVE]: {
    icon: FiClock,
    status: FindingStatus.ACTIVE,
    title: 'findings.states.active',
  },
  [FindingStatus.PROCESSING]: {
    icon: LuSend,
    status: FindingStatus.PROCESSING,
    title: 'findings.states.waiting',
  },
  [FindingStatus.PROCESSED]: {
    icon: FaRegCheckCircle,
    status: FindingStatus.PROCESSED,
    title: 'findings.states.processed',
  },
  [FindingStatus.DELETED]: {
    icon: RiDeleteBinLine,
    status: FindingStatus.DELETED,
    title: 'findings.states.deleted',
  },
};

interface FindingsContentProps {
  findings: Finding[];
  findingsCount: number;
  contractors: string[];
  uniqueBlobs: FindingBlob[];
  commenters: FindingCommenter[];
  uniqueLabels: string[];
  statusTabs: FindingStatus[];
  statusCount: Record<string, number>;
  itemsOnPage: number;
  isFetchingFindings: boolean;
  isFetchingStatusCount: boolean;
  downloadFindingsForExport: (isFilterNeeded: boolean) => Promise<Finding[]>;
  isDownloadingFindingsForExport: boolean;
}
export const FindingsContent = ({
  findings,
  findingsCount,
  contractors,
  uniqueBlobs,
  commenters,
  uniqueLabels,
  statusTabs,
  statusCount,
  itemsOnPage,
  isFetchingFindings,
  isFetchingStatusCount,
  downloadFindingsForExport,
  isDownloadingFindingsForExport,
}: FindingsContentProps) => {
  const { t } = useTranslation();
  const {
    selectedConflictCategory,
    selectedConflictType,
    selectedContractor,
    setSelectedContractor,
    setPage,
  } = useFindingsContext();
  const writeQueryParam = useWriteQueryParam();

  const handleSelectContractor = useCallback(
    (contractor: string | undefined) => {
      setSelectedContractor(contractor);
      setPage(1);
      writeQueryParam('reference', contractor);
    },
    [setSelectedContractor, setPage, writeQueryParam],
  );

  const getTitleMessage = useMemo(() => {
    if (selectedConflictCategory && selectedConflictType) {
      return t(`dashboard.conflict-title.${selectedConflictType}`);
    } else if (selectedConflictCategory) {
      return t(`dashboard.conflict-category.${selectedConflictCategory}`);
    }
    return t('findings.total_findings');
  }, [selectedConflictCategory, selectedConflictType, t]);

  const totalStatusCount = useMemo(() => {
    return statusCount ? Object.values(statusCount).reduce((a, b) => a + b, 0) : 0;
  }, [statusCount]);

  return (
    <div className="p-0 lg:px-8 flex-1 bg-day-light-3 transition duration-500">
      <DismissibleDisclaimer>{t('findings.ai-disclaimer')}</DismissibleDisclaimer>
      <div className="flex flex-col">
        <div className="px-4 py-4 2xl:py-6 relative">
          <div className="flex items-center justify-between">
            <ServiceHeader
              icon={TbFileSearch}
              count={totalStatusCount}
              message={t('findings.findings')}
              title={getTitleMessage}
            />
            <div className="px-4">
              <ExportButton
                isFilterNeeded={true}
                downloadFindings={downloadFindingsForExport}
                isDownloadingFindings={isDownloadingFindingsForExport}
              />
            </div>
          </div>
          {contractors.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-day-neutral-subtle">
                {t('bid-review.dashboard.view-contractor')}
              </span>
              <button
                type="button"
                onClick={() => handleSelectContractor(undefined)}
                className={clsx(
                  'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  !selectedContractor
                    ? 'bg-day-neutral-dark text-white'
                    : 'bg-day-light-3 text-day-neutral-subtle hover:bg-day-light-4',
                )}
              >
                {t('findings.all-contractors')}
              </button>
              {contractors.map((contractor) => (
                <button
                  key={contractor}
                  type="button"
                  onClick={() => handleSelectContractor(contractor)}
                  className={clsx(
                    'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                    selectedContractor === contractor
                      ? 'bg-day-neutral-dark text-white'
                      : 'bg-day-light-3 text-day-neutral-subtle hover:bg-day-light-4',
                  )}
                >
                  {contractor}
                </button>
              ))}
            </div>
          )}
          <FindingsList
            findings={findings}
            uniqueBlobs={uniqueBlobs}
            commenters={commenters}
            uniqueLabels={uniqueLabels}
            findingsCount={findingsCount}
            statusTabs={statusTabs}
            statusCount={statusCount}
            itemsOnPage={itemsOnPage}
            isFetchingFindings={isFetchingFindings}
            isFetchingStatusCount={isFetchingStatusCount}
          />
        </div>
      </div>
    </div>
  );
};
